import { isPlatformBrowser } from '@angular/common';
import {
	Component,
	ElementRef,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { Chart, registerables, LayoutPosition } from 'chart.js';
import { TranslateService } from '@ngx-translate/core';
import 'chartjs-plugin-annotation';
import { DecimalPipe } from '@angular/common';
import { ProductService } from '@app-services/product.service';
import { take } from 'rxjs/operators';
Chart.register(...registerables);

@Component({
	selector: 'app-line-chart',
	templateUrl: './line-chart.component.html',
	styleUrls: ['./line-chart.component.scss']
})
export class LineChartComponent implements OnInit, OnChanges, OnDestroy {
	@ViewChild('chartCanvas', { static: true })
	private readonly chartCanvas?: ElementRef<HTMLCanvasElement>;

	private readonly isBrowser: boolean;
	private chartInstance: Chart | null = null;
	private labelTranslationsInitialized = false;

	@Input() labels: string[];
	@Input() label: string;
	@Input() data: number[];
	@Input() averageData: number[];
	@Input() typeChart: any = 'line';
	@Input() positionChart: LayoutPosition = 'bottom';

	labelLine: string;
	labelAverage: string;
	stepSize: number;

	private decimalPipe: DecimalPipe;

	constructor(
		private translate: TranslateService,
		private productService: ProductService,
		@Inject(PLATFORM_ID) platformId: object
	) {
		this.isBrowser = isPlatformBrowser(platformId);
	}

	ngOnInit(): void {
		this.productService
			.getProductInfo()
			.pipe(take(1))
			.subscribe(p => {
				this.decimalPipe = new DecimalPipe(p.language);
				this.maybeRenderChart();
			});

		if (!this.labelTranslationsInitialized) {
			this.loadLabelTranslations();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!this.isBrowser) {
			return;
		}

		if (
			changes['labels'] ||
			changes['data'] ||
			changes['averageData'] ||
			changes['positionChart'] ||
			changes['typeChart']
		) {
			this.maybeRenderChart();
		}

		if (changes['label']) {
			this.loadLabelTranslations();
		}
	}

	ngOnDestroy(): void {
		this.chartInstance?.destroy();
		this.chartInstance = null;
	}

	private loadLabelTranslations(): void {
		this.labelTranslationsInitialized = true;

		this.translate
			.get(`stats.${this.label}`)
			.pipe(take(1))
			.subscribe(s => {
				this.labelLine = s;
				this.maybeRenderChart();
			});

		this.translate
			.get('dashboard.average_value')
			.pipe(take(1))
			.subscribe(s => {
				this.labelAverage = s;
				this.maybeRenderChart();
			});
	}

	private maybeRenderChart(): void {
		if (!this.isBrowser) {
			return;
		}
		if (!this.data || !this.labels || !this.labelLine || !this.labelAverage) {
			if (this.chartInstance) {
				this.chartInstance.destroy();
				this.chartInstance = null;
			}
			return;
		}
		this.handleInitChartjs();
	}

	private handleInitChartjs(): void {
		if (!this.isBrowser) {
			return;
		}
		const canvasEl = this.chartCanvas?.nativeElement;
		if (!canvasEl) {
			return;
		}

		try {
			const printWrapper = canvasEl?.closest('.print-page__scale') as HTMLElement | null;
			const isPrintContext = Boolean(printWrapper);
			const baseFontSizePx = isPrintContext
				? Number.parseFloat(getComputedStyle(printWrapper as Element).fontSize)
				: Number.NaN;

			const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
			const resolvedBase = Number.isFinite(baseFontSizePx) ? baseFontSizePx : 14;
			const legendFontSize = isPrintContext ? clamp(Math.round(resolvedBase * 1.4), 6, 14) : 14;
			const tickFontSize = isPrintContext ? clamp(Math.round(resolvedBase * 1.2), 6, 12) : 12;
			const legendLineHeight = isPrintContext ? Math.round(legendFontSize * 1.6) : 22;
			const legendPadding = isPrintContext ? Math.round(resolvedBase * 2.4) : 20;
			const legendBoxHeight = isPrintContext ? clamp(Math.round(resolvedBase * 0.8), 3, 7) : 7;
			const legendPointWidth = isPrintContext ? clamp(Math.round(resolvedBase * 1.2), 6, 10) : 10;

			this.chartInstance?.destroy();
			this.chartInstance = new Chart(canvasEl, {
				type: 'bar',
				data: {
					labels: this.labels,
					datasets: [
						{
							label: this.labelLine,
							data: this.data,
							type: 'bar',
							backgroundColor: '#F18700',
							borderColor: '#F18700',
							borderWidth: 1,
							borderRadius: 4,
							barPercentage: 0.6,
							categoryPercentage: 0.8
						},
						{
							label: this.labelAverage,
							data: this.averageData,
							type: 'line',
							fill: false,
							borderColor: '#145E7F',
							pointBackgroundColor: '#145E7F',
							borderWidth: 2,
							borderDash: [5, 5],
							pointRadius: 0
						}
					]
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					plugins: {
						legend: {
							position: this.positionChart,
							display: true,
							labels: {
								padding: legendPadding,
								font: {
									size: legendFontSize,
									weight: 400,
									lineHeight: legendLineHeight,
									family: 'ZT Nature'
								},
								usePointStyle: true,
								boxHeight: legendBoxHeight,
								pointStyleWidth: legendPointWidth
							}
						},
						tooltip: {
							callbacks: {
								label: context => {
									const value = context.parsed.y;
									const digits =
										value > 10 || Number.isInteger(value)
											? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
											: { minimumFractionDigits: 1, maximumFractionDigits: 1 };
									const formattedValue =
										this.decimalPipe?.transform(value, value > 10 || Number.isInteger(value) ? '0.0-0' : '0.1-1') ??
										new Intl.NumberFormat(undefined, digits).format(value);
									return context.dataset.label + ': ' + formattedValue;
								}
							}
						},
						datalabels: {
							display: false
						}
					},
					scales: {
						x: {
							beginAtZero: true,
							ticks: {
								font: {
									size: tickFontSize
								}
							}
						},
						y: {
							beginAtZero: true,
							title: {
								display: false
							},
							ticks: {
								maxTicksLimit: 5,
								font: {
									size: tickFontSize
								},
								stepSize: 4, // Defina o intervalo dos valores no eixo Y
								callback: function (value) {
									return value.toString();
								}
							}
						}
					}
				}
			});
		} catch (error) {
			if (!this.isBrowser) {
				console.warn('Skipping line chart render (SSR/DOM not supported).', error);
			} else {
				console.error('Failed to render LineChart: Chart.js configuration error', {
					error,
					label: this.label,
					labelsCount: this.labels?.length,
					dataCount: this.data?.length
				});
			}
			this.chartInstance?.destroy();
			this.chartInstance = null;
		}
	}
}
